import styled from '@emotion/styled'
import { useTheme } from '@emotion/react'

import Image from '@segment/matcha/components/Image'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import { TwilioMonoLogo } from '@segment/matcha/components/CompanyLogos/CompanyMono'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import TestimonialBase, * as Testimonial from '@segment/matcha/recipes/Testimonial'

import chatbotsDoughnut from './assets/ChatbotsDoughnut.png'
import chatbotsDoughnutWebp from './assets/ChatbotsDoughnut.webp'
import chatbotsDoughnutWebp2x from './assets/ChatbotsDoughnut@2x.webp'
import chatbotsBarCharts from './assets/ChatbotsBarCharts.png'
import chatbotsBarChartsWebp from './assets/ChatbotsBarCharts.webp'
import chatbotsBarChartsWebp2x from './assets/ChatbotsBarCharts@2x.webp'
import thomasWyattAvatar from './assets/ThomasWyatt.png'

const copy = {
  heading: `Our survey respondents envision a landscape where chatbots do more than just respond—they engage, understand, and anticipate the needs of users with a finesse that mimics a human touch.`,
  description: `The expected rise of chatbots signifies a shift towards more dynamic and intelligent platforms that can manage complex customer relationships at scale, suggesting that the next big leap in personalization will be through conversational AI that knows just what to say, and just as importantly, when to say it.`,
}

const testimonialData = {
  text: `“Personalized interactions have long been a priority for companies. Recent advances in generative AI offer more than just a step-change in personalization, but a true transformative and generational change in how businesses engage customers. Now is the time for businesses to build a strong customer data foundation to seize the moment and exceed their customers’ expectations.”`,
  name: `Thomas Wyatt`,
  position: `President, Twilio Segment`,
}

export const Chatbots = () => {
  const theme = useTheme()

  return (
    <Wrapper>
      <SectionBase variant="centered">
        <Section.Copy>
          <CopyTextBase centered={true}>
            <CopyText.Heading tag="h2">{copy.heading}</CopyText.Heading>
            <CopyText.Description tag="p">{copy.description}</CopyText.Description>
          </CopyTextBase>
        </Section.Copy>

        <Section.Contents>
          <StyledColumnTrack columns={1} overrides={{ md: 2 }}>
            <picture>
              <source type="image/webp" srcSet={`${chatbotsDoughnutWebp}, ${chatbotsDoughnutWebp2x} 2x`} />
              <Image
                width="500"
                height="617"
                src={chatbotsDoughnut}
                alt="89% of leaders believe personalization is crucial to their business’ success in the next three years"
              />
            </picture>

            <picture>
              <source type="image/webp" srcSet={`${chatbotsBarChartsWebp}, ${chatbotsBarChartsWebp2x} 2x`} />
              <Image
                width="500"
                height="617"
                src={chatbotsBarCharts}
                alt="89% of leaders believe personalization is crucial to their business’ success in the next three years"
              />
            </picture>
          </StyledColumnTrack>
        </Section.Contents>
      </SectionBase>

      <StyledTestimonial>
        <Testimonial.Base height="auto" tokens={{ background: theme.palette.twilio.gray[100] }}>
          <Testimonial.BaseContentSlot>
            <Testimonial.BaseLogo>
              <TwilioMonoLogo color={theme.palette.neutrals.white[100]} />
            </Testimonial.BaseLogo>
            <Testimonial.BaseText>{testimonialData.text}</Testimonial.BaseText>
          </Testimonial.BaseContentSlot>
          <Testimonial.BaseAuthorSlot>
            <Testimonial.AuthorAvatar width={48} height={48} alt="" src={thomasWyattAvatar} />
            <Testimonial.AuthorInfo>
              <Testimonial.AuthorName>{testimonialData.name}</Testimonial.AuthorName>
              <Testimonial.AuthorPosition>{testimonialData.position}</Testimonial.AuthorPosition>
            </Testimonial.AuthorInfo>
          </Testimonial.BaseAuthorSlot>
        </Testimonial.Base>
      </StyledTestimonial>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  padding-bottom: 80px;
  background-color: ${({ theme }) => theme.palette.neutrals.white[100]};

  ${({ theme }) => theme.breakpoints.down(`md`)} {
    padding-bottom: 40px;
  }
`

const StyledColumnTrack = styled(ColumnTrack)`
  max-width: 1032px;
  margin: 0 auto;
`

const StyledTestimonial = styled(TestimonialBase)`
  max-width: 1040px;
  margin: 0 auto;
`

export default Chatbots
